//#region imports
import React, { useState, useEffect } from 'react';
import { firestore } from '_utils/firebase-app';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';

// bootstrap
import { Alert, Card, Form, Button } from 'react-bootstrap';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { updateFlight, selectUpdateFlightStatus } from '_redux/slices/timeline/timeline-flight-list';
import { selectFirebaseUser, selectIata } from '_redux/slices/main-screen/user';
import { patchFlightSchedule, resetPatchflightFlags, selectPatchFlightStatus } from '_redux/slices/flights/flight-information';
import { selectTimelineIndex } from '_redux/slices/timeline/timeline-control';
import { setInternalNotify } from '_redux/slices/timeline/timeline-toast';

// icons
import { MdFlight, MdInfo, MdSchedule, MdNotes, MdLockOpen, MdLockOutline } from 'react-icons/md';

// styles
import { Spacing, Colors } from '_styles';

// components
import { CustomSpinner } from '_atoms';

// task colors
import * as TaskColors from '../../timeline/utils/colors';
import { GRAY_LIGHT, GRAY_DARK, TEXT_COLOR } from '_styles/colors';

// utils
import { capitalizeWords, hhmm, setHHmm } from '_utils/functions';
import { getBoardingOffsetMinutes } from '../utils/flight-time';

// moment
import moment from 'moment';

//#endregion

//#region styles
const CARD_STYLE = {
    marginBottom: Spacing.SCALE_16,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
};

const ROW_STYLE = {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    gap: Spacing.SCALE_16,
    flexWrap: 'wrap',
};

const ITEM_STYLE = {
    flex: 1,
    minWidth: '120px',
    textAlign: 'flex-start',
    backgroundColor: 'transparent',
    border: '2px solid transparent',
    borderRadius: '6px',
    padding: 0,
    transition: 'all 0.2s ease',
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: '#374151',
    margin: 0,
    fontWeight: 500,
};

const CONTENT_STYLE = { padding: Spacing.SCALE_16 };
//#endregion

//#region helper components
const InfoItem = ({ label, children }) => (
    <div style={ITEM_STYLE}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children}</div>
    </div>
);

const normalizeFlightNotes = (flightNotes) => {
    if (Array.isArray(flightNotes)) {
        return flightNotes.filter((note) => note && note.text);
    }

    if (typeof flightNotes === 'string' && flightNotes.trim()) {
        return [{ text: flightNotes.trim() }];
    }

    return [];
};

const formatFlightNoteTimestamp = (note) => {
    const timestamp = note?.time || note?.timestamp;
    return timestamp ? moment(new Date(timestamp)).format('DD/MM/YYYY HH:mm') : '';
};

const getTimeValue = (isoDate) => {
    const timeValue = new Date(isoDate).getTime();
    return Number.isNaN(timeValue) ? null : timeValue;
};
//#endregion

const RC_REPORT_LABELS = {
    not_collected: 'Not Collected',
    no_longer_needed: 'No Longer Needed',
    other: 'Other',
};

const FlightInformationCard = ({
    flightDocument,
    onUpdate,
    bookingId,
    isRC,
}) => {
    const dispatch = useDispatch();
    const updateFlightStatus = useSelector(selectUpdateFlightStatus);
    const firebaseUser = useSelector(selectFirebaseUser);
    const patchFlightStatus = useSelector(selectPatchFlightStatus);
    const iata = useSelector(selectIata) || '';
    const timeIndex = useSelector(selectTimelineIndex);
    const manualLockInitial = !!flightDocument?.manualLock;
    const manualLockBoardingInitial = flightDocument?.manualLockBoarding;
    const displayedIso = manualLockInitial
        ? flightDocument?.operationTime
        : flightDocument?.flightFeedTime

    const boardingOffsetMinutes = getBoardingOffsetMinutes(flightDocument, iata);
    const displayedBoardingIso = manualLockBoardingInitial
        ? flightDocument?.manualBoardingTime
        : (flightDocument?.boardingTime
            ? flightDocument.boardingTime
            : new Date(new Date(flightDocument?.operationTime ?? flightDocument?.flightFeedTime ?? new Date())?.getTime() - boardingOffsetMinutes * 60 * 1000)?.toISOString())
    // Notes state
    const [rcReports, setRcReports] = useState([]);

    useEffect(() => {
        if (!isRC || !bookingId) return;
        const q = query(
            collection(firestore, 'remotecheckin_sessions'),
            where('bookingId', '==', bookingId),
            limit(1)
        );
        const unsub = onSnapshot(q, (snap) => {
            if (snap.empty) return setRcReports([]);
            const data = snap.docs[0].data();
            setRcReports(Array.isArray(data.reports) ? data.reports : []);
        });
        return () => unsub();
    }, [bookingId, isRC]);

    const [noteInput, setNoteInput] = useState('');
    const [flightNotes, setFlightNotes] = useState([]);
    const [pendingFlightNotes, setPendingFlightNotes] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [manualLock, setManualLock] = useState(manualLockInitial);
    const [tempTime, setTempTime] = useState(hhmm(displayedIso));

    const [currentTime, setCurrentTime] = useState(hhmm(displayedIso));
    const [currentBoardingTime, setCurrentBoardingTime] = useState(hhmm(displayedBoardingIso));
    const [manualLockBoarding, setManualLockBoarding] = useState(false);
    const [tempBoardingTime, setTempBoardingTime] = useState(hhmm(displayedBoardingIso));
    const [lockBoardingError, setLockBoardingError] = useState(null);
    const [boardingAfterScheduleTime, setBoardingAfterScheduleTime] = useState(false);
    const [boardingLockNotificationPending, setBoardingLockNotificationPending] = useState(false);



    // Initialize notes from flight document
    useEffect(() => {
        if (!flightDocument) return;
        setFlightNotes(normalizeFlightNotes(flightDocument.notes));
        setNoteInput('');
        setPendingFlightNotes(null);

        setManualLock(flightDocument.manualLock);
        setCurrentTime(hhmm(displayedIso));
        setCurrentBoardingTime(hhmm(displayedBoardingIso));
        setManualLockBoarding(flightDocument.manualLockBoarding);
    }, [flightDocument]);

    useEffect(() => {
        if (flightDocument?.type !== "boarding") {
            setBoardingAfterScheduleTime(false);
            return;
        }

        const scheduledTime = getTimeValue(flightDocument?.scheduled);
        if (scheduledTime == null) {
            setBoardingAfterScheduleTime(false);
            return;
        }

        const boardingIso = manualLockBoarding
            ? (flightDocument?.manualBoardingTime || displayedBoardingIso)
            : setHHmm(
                displayedBoardingIso || flightDocument?.operationTime || flightDocument?.flightFeedTime || flightDocument?.scheduled,
                tempBoardingTime
            );
        const boardingTime = getTimeValue(boardingIso);
        const isAfterSchedule = boardingTime != null && boardingTime > scheduledTime;

        setBoardingAfterScheduleTime(isAfterSchedule);
    }, [
        displayedBoardingIso,
        flightDocument?.flightFeedTime,
        flightDocument?.manualBoardingTime,
        flightDocument?.operationTime,
        flightDocument?.scheduled,
        flightDocument?.type,
        flightDocument?._id,
        flightDocument?.flightId,
        manualLockBoarding,
        tempBoardingTime,
        dispatch,
        flightDocument?.flightNumber,
        flightDocument?.departureAirport,
        flightDocument?.arrivalAirport,
    ]);

    // Watch for successful update and refresh flights (only when we're actually saving)
    useEffect(() => {
        if (isSaving && updateFlightStatus === 'succeeded') {
            if (pendingFlightNotes) {
                setFlightNotes(pendingFlightNotes);
            }
            setPendingFlightNotes(null);
            setNoteInput('');
            setSaveSuccess(true);
            setIsEditing(false);
            setIsSaving(false);

            // Call onUpdate to refresh flights
            if (onUpdate) {
                onUpdate();
            }

            // Clear success message after 2 seconds
            setTimeout(() => setSaveSuccess(false), 2000);
        }
        if (isSaving && updateFlightStatus === 'failed') {
            setPendingFlightNotes(null);
            setIsSaving(false);
        }
    }, [updateFlightStatus, isSaving, onUpdate, pendingFlightNotes]);

    // after patch flight success, refetch flight list & reset selected flight
    useEffect(() => {
        if (patchFlightStatus === "succeeded") {
            if (boardingLockNotificationPending) {
                const flightRef = flightDocument?.flightNumber || flightDocument?.departureAirport || flightDocument?.arrivalAirport || 'Unknown flight';
                const flightId = flightDocument?._id || flightDocument?.flightId || flightRef;
                dispatch(setInternalNotify({
                    id: flightId,
                    flightId,
                    title: 'Boarding after scheduled time',
                    body: `Flight ${flightRef} has a boarding time after its scheduled time.`,
                }));
                setBoardingLockNotificationPending(false);
            }
            dispatch(resetPatchflightFlags());
            if (onUpdate) {
                onUpdate();
            }
        }
        if (patchFlightStatus === "failed") {
            setBoardingLockNotificationPending(false);
        }
    }, [patchFlightStatus, boardingLockNotificationPending, dispatch, flightDocument, onUpdate]);

    // // for Boarding flights - set lock boarding error if tempBoardingTime is out of timeline range
    // useEffect(() => {
    //     if (!tempBoardingTime || !Array.isArray(timeIndex) || timeIndex.length === 0) return;

    //     // split tempBoardingTime and format in mins
    //     const [tH, tM] = tempBoardingTime.split(':').map(Number);
    //     const tempMinutes = tH * 60 + tM;

    //     // first and last time index of timeline range
    //     const [fH, fM] = timeIndex[0].split(':').map(Number);
    //     const firstMinutes = fH * 60 + fM;

    //     const [lH, lM] = timeIndex[timeIndex.length - 1].split(':').map(Number);
    //     const lastMinutes = lH * 60 + lM + 60; // end of the last slot

    //     // check if the tempBoardingTime is within or not
    //     // When timeline wraps midnight example 19:00 to 06:30, valid range is: tempMinutes >= firstMinutes OR tempMinutes <= lastMinutes.
    //     const wrapsMidnight = firstMinutes > lastMinutes;
    //     const isOutOfRange = wrapsMidnight
    //         ? (tempMinutes < firstMinutes && tempMinutes > lastMinutes)
    //         : (tempMinutes < firstMinutes || tempMinutes >= lastMinutes);

    //     if (isOutOfRange) {
    //         setLockBoardingError("Time is outside of timeline range");
    //     } else {
    //         setLockBoardingError(null);
    //     }
    // }, [tempBoardingTime, timeIndex])

    // for Boarding flights - set lock boarding error if tempBoardingTime is out of timeline range
    useEffect(() => {
        if (!tempBoardingTime || !Array.isArray(timeIndex) || timeIndex.length === 0) return;

        // normalize "HH:mm" into minutes since midnight (0..1439).
        const parseMinutes = (time) => {
            const [h, m] = String(time).split(':').map(Number);
            return (h * 60 + m) % 1440;
        };

        const tempMinutes = parseMinutes(tempBoardingTime);
        const slotStarts = timeIndex.map(parseMinutes).filter((m) => Number.isFinite(m));
        if (slotStarts.length === 0) return;

        // infer slot size from timeline spacing; fallback to 60 minutes.
        const slotSize =
            slotStarts.length > 1
                ? ((slotStarts[1] - slotStarts[0] + 1440) % 1440) || 60
                : 60;

        // validate by checking if the selected time falls inside ANY timeline slot.
        // handles normal ranges and midnight-wrapping ranges (e.g., 19:00 -> 06:00).
        const isWithinAnySlot = slotStarts.some((start) => {
            const end = (start + slotSize) % 1440;
            // wrapped slot over midnight, e.g. 23:00 -> 00:00.
            if (start > end) {
                return tempMinutes >= start || tempMinutes < end;
            }
            return tempMinutes >= start && tempMinutes < end;
        });

        const isOutOfRange = !isWithinAnySlot;

        if (isOutOfRange) {
            setLockBoardingError("Time is outside of timeline range");
        } else {
            setLockBoardingError(null);
        }
    }, [tempBoardingTime, timeIndex])

    // Handle save notes
    const handleSaveNotes = () => {
        let id = flightDocument?._id || flightDocument?.flightId;
        const trimmedNote = noteInput.trim();

        if (!id || !trimmedNote) return;

        setIsSaving(true);

        // Create note entry with user info
        const noteEntry = {
            time: new Date().toISOString(),
            text: trimmedNote,
            userId: firebaseUser?.uid || firebaseUser?.id || "unknown",
            userName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
        };
        const updatedFlightNotes = [...flightNotes, noteEntry];

        setPendingFlightNotes(updatedFlightNotes);

        dispatch(updateFlight({
            id: id,
            body: {
                notes: updatedFlightNotes,
            },
        }));
    };

    const lockAndSave = () => {
        const id = flightDocument?._id ?? flightDocument?.flightId;
        const newManualTimeISO = setHHmm(
            flightDocument?.operationTime || flightDocument?.flightFeedTime,
            tempTime
        );

        const payload = {
            id,
            body: {
                manualLock: true,
                operationInfo: {
                    status: flightDocument?.status,
                    statusType: flightDocument?.statusType,
                    time: newManualTimeISO, // user-chosen time (manual)
                },
                flightFeedTime: statusTime, // last feed time
                manualLockedBy: {
                    name: firebaseUser?.displayName || 'unknown',
                    email: firebaseUser?.email || 'unknown',
                    timestamp: new Date().toISOString(),
                }
            },
        };

        setCurrentTime(tempTime);

        dispatch(patchFlightSchedule(payload));
        setManualLock(true);
    };


    // PATCH:2 unlock & resume feed time, use old approach
    const unlock = () => {
        const id = flightDocument?._id ?? flightDocument?.flightId;

        const payload = {
            id,
            body: {
                manualLock: false,
                // immediately set op time back to feed time (optional; or omit and let server/feed update)
                operationInfo: {
                    status: flightDocument?.status,
                    statusType: flightDocument?.statusType,
                    time: flightDocument?.flightFeedTime,
                },
                flightFeedTime: flightDocument?.flightFeedTime,
                manualUnlockedBy: {
                    name: firebaseUser?.displayName || 'unknown',
                    email: firebaseUser?.email || 'unknown',
                    timestamp: new Date().toISOString(),
                }
            },
        };

        dispatch(patchFlightSchedule(payload));
        setManualLock(false);
        // also reflect UI picker back to the feed time
        setTempTime(hhmm(flightDocument?.flightFeedTime || statusTime));
        setCurrentTime(hhmm(flightDocument?.flightFeedTime || statusTime));
    };

    const lockAndSaveBoarding = () => {

        if (lockBoardingError) return;

        const scheduledTime = getTimeValue(flightDocument?.scheduled);
        const boardingIso = manualLockBoarding
            ? (flightDocument?.manualBoardingTime || displayedBoardingIso)
            : setHHmm(
                displayedBoardingIso || flightDocument?.operationTime || flightDocument?.flightFeedTime || flightDocument?.scheduled,
                tempBoardingTime
            );
        const boardingTime = getTimeValue(boardingIso);
        const isAfterSchedule = boardingTime != null && scheduledTime != null && boardingTime > scheduledTime;

        setBoardingLockNotificationPending(isAfterSchedule);

        const id = flightDocument?._id ?? flightDocument?.flightId;
        const newManualTimeISO = setHHmm(
            displayedBoardingIso || flightDocument?.operationTime || flightDocument?.flightFeedTime,
            tempBoardingTime
        );

        const payload = {
            id,
            body: {
                manualLockBoarding: true,
                manualBoardingTime: newManualTimeISO,
                manualLockedBoardingBy: {
                    name: firebaseUser?.displayName || 'unknown',
                    email: firebaseUser?.email || 'unknown',
                    timestamp: new Date().toISOString(),
                }
            },
        };

        setCurrentBoardingTime(hhmm(newManualTimeISO));

        dispatch(patchFlightSchedule(payload));
        setManualLockBoarding(true);
    };

    const unlockBoarding = () => {
        const id = flightDocument?._id ?? flightDocument?.flightId;

        const payload = {
            id,
            body: {
                manualLockBoarding: false,
                manualBoardingTime: null,
                manualUnlockedBoardingBy: {
                    name: firebaseUser?.displayName || 'unknown',
                    email: firebaseUser?.email || 'unknown',
                    timestamp: new Date().toISOString(),
                }
            },
        };

        dispatch(patchFlightSchedule(payload));
        setManualLockBoarding(false);
        // reflect UI picker back to computed boarding time (operation time - offset)
        const baseIso = flightDocument?.flightFeedTime || statusTime;
        const boardingTimeIso = baseIso ? new Date(new Date(baseIso).getTime() - boardingOffsetMinutes * 60 * 1000).toISOString() : null;
        setTempBoardingTime(hhmm(boardingTimeIso));
        setCurrentBoardingTime(hhmm(boardingTimeIso));
        setBoardingAfterScheduleTime(false);
    };

    const onPrimaryClick = () => (manualLock ? unlock() : lockAndSave());

    const onPrimaryClickBoarding = () => (manualLockBoarding ? unlockBoarding() : lockAndSaveBoarding());


    // Calculate flight type from document
    const flightType = flightDocument?.type;

    // Calculate operating status from flight document
    const operatingStatus = flightDocument?.operationInfo?.status || flightDocument?.status;

    // Calculate flight status based on operation type
    let flightStatus = "Scheduled";
    if (flightDocument?.operationInfo?.status === "Actual" || flightDocument?.status === "Actual") {
        const statusType = flightDocument?.operationInfo?.statusType || flightDocument?.statusType;
        if (statusType === "Out of Blocks" || statusType === "Taken Off" || statusType === "Take Off" || statusType === "On Blocks" || statusType === "Touchdown") {
            if (flightDocument?.type === "departure") {
                flightStatus = "Departed";
            }
            if (flightDocument?.type === "arrival") {
                flightStatus = "Landed";
            }
        }
    }

    // Calculate formatted time
    const statusTime = flightDocument?.type === "boarding" && flightDocument?.manualLockBoarding && flightDocument?.manualBoardingTime
        ? flightDocument?.manualBoardingTime
        : flightDocument?.operationInfo?.time || flightDocument?.operationTime;
    const formattedTime = statusTime ? moment(new Date(statusTime)).format("HH:mm") : null;

    // Calculate original time
    const originalTime = flightDocument?.originalFlightTime ? moment(new Date(flightDocument?.originalFlightTime)).format('HH:mm') : null;

    // Check if time difference should be shown
    const showTimeDifference = formattedTime && originalTime && (formattedTime !== originalTime);

    // Always show extended info for detailed view
    const showExtendedInfo = true;

    // Code share flights
    const codeShareFlights = Array.isArray(flightDocument?.CodeShareFlights) && flightDocument.CodeShareFlights.length > 0
        ? flightDocument.CodeShareFlights.join(', ')
        : null;

    // Check if aircraft info exists
    const hasAircraftInfo = flightDocument?.aircraftRegistration || flightDocument?.aircraftType || flightDocument?.aircraftModel;

    // Set background color based on flight type (same logic as card-bottom.js)
    const getHeaderBackgroundColor = (flightType) => {
        if (flightType?.toLowerCase() === 'boarding') {
            return TaskColors.combinedBooking; // '#e895a4' - pinkish color for boarding
        } else if (flightType?.toLowerCase() === 'departure') {
            return Colors.PRM_LIGHT_BLUE; // blue for departures
        } else {
            return Colors.ARRIVALS_GREEN; // '#29C40D' - green for arrivals
        }
    };

    const headerBackgroundColor = getHeaderBackgroundColor(flightType);

    return (
        <>

            {/* Operation Info Card */}
            {showExtendedInfo && (operatingStatus || flightStatus || formattedTime) && (
                <Card style={CARD_STYLE}>
                    <Card.Header style={{ backgroundColor: headerBackgroundColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdSchedule
                                size={20}
                                color="white"
                            />
                            <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>Operation Info</p>
                        </div>
                    </Card.Header>
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={ROW_STYLE}>
                            <InfoItem label="Operation Status">{operatingStatus ? capitalizeWords(operatingStatus) : 'N/A'}</InfoItem>
                            <InfoItem label="Operation Time">{formattedTime || 'N/A'}</InfoItem>
                            {flightDocument?.type === 'boarding' && (
                                <InfoItem label="Boarding Time">
                                    {displayedBoardingIso ? `${moment(new Date(displayedBoardingIso)).format("HH:mm")} (-${boardingOffsetMinutes} min)` : 'N/A'}
                                </InfoItem>
                            )}
                            <InfoItem label="Flight Status">{flightStatus ? capitalizeWords(flightStatus) : 'N/A'}</InfoItem>
                            <InfoItem label="Scheduled Time">{moment(new Date(flightDocument?.scheduled)).format("HH:mm") || 'N/A'}</InfoItem>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Boarding Time Locking - directly under Operation Info */}
            {flightDocument?.type === "boarding" && (
                <Card style={{
                    border: `1px solid ${GRAY_LIGHT}`,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: 'white',
                    marginBottom: Spacing.SCALE_16
                }}>
                    <Card.Header style={{
                        backgroundColor: manualLockBoarding ? '#28a745' : '#ffc107',
                        color: 'white',
                        padding: Spacing.SCALE_12,
                        borderRadius: '8px 8px 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {manualLockBoarding ? 'Boarding Time Locked' : 'Boarding Time Unlocked'}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {manualLockBoarding ? (
                                <MdLockOutline style={{ fontSize: 20 }} />
                            ) : (
                                <MdLockOpen style={{ fontSize: 20 }} />
                            )}
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: Spacing.SCALE_16 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: Spacing.SCALE_16,
                            marginBottom: Spacing.SCALE_16
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: GRAY_DARK,
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Current Boarding Time
                                </div>
                                <div style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: TEXT_COLOR
                                }}>
                                    {currentBoardingTime}
                                </div>
                            </div>

                            {!manualLockBoarding && (
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: GRAY_DARK,
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        New Boarding Time
                                    </div>
                                    <Form.Control
                                        type="time"
                                        value={tempBoardingTime}
                                        step={60}
                                        onChange={(e) => setTempBoardingTime(e.target.value)}
                                        style={{
                                            maxWidth: 150,
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            border: lockBoardingError ? '1px solid #dc3545' : "1px solid #dee2e6"
                                        }}
                                    />
                                    {lockBoardingError && (
                                        <span style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px', display: 'block' }}>
                                            {lockBoardingError}
                                        </span>
                                    )}
                                    {boardingAfterScheduleTime && (
                                        <Alert variant="warning" style={{ marginTop: Spacing.SCALE_8, marginBottom: 0, padding: '6px 10px', fontSize: 12 }}>
                                            Boarding time is after the scheduled time.
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </div>

                        {manualLockBoarding && boardingAfterScheduleTime && (
                            <Alert variant="warning" style={{ marginBottom: Spacing.SCALE_16, padding: '6px 10px', fontSize: 12 }}>
                                Boarding time is after the scheduled time, original boarding time is {hhmm(flightDocument?.boardingTime)}.
                            </Alert>
                        )}

                        <Button
                            variant={manualLockBoarding ? "outline-danger" : "primary"}
                            onClick={onPrimaryClickBoarding}
                            disabled={patchFlightStatus === "loading" || lockBoardingError}
                            style={{
                                minWidth: 150,
                                fontWeight: '600'
                            }}
                        >
                            {patchFlightStatus === "loading" ? (
                                <CustomSpinner />
                            ) : (
                                manualLockBoarding ? 'Unlock Boarding Time' : 'Lock Boarding Time'
                            )}
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {/* Gate + Terminal Information */}
            <Card style={CARD_STYLE}>

                {/* Header */}
                <Card.Header style={{ backgroundColor: headerBackgroundColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdFlight
                            size={20}
                            color="white"
                            style={{}}
                        />
                        <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>Gate + Terminal Information</p>
                    </div>
                </Card.Header>

                {/* Body */}
                <Card.Body style={CONTENT_STYLE}>

                    {/* Terminal/Gate/Stand Row */}
                    <div style={{ ...ROW_STYLE }}>
                        <InfoItem label="Terminal">{flightDocument?.terminal || 'N/A'}</InfoItem>
                        <InfoItem label="Gate">{flightDocument?.gate || 'N/A'}</InfoItem>
                        <InfoItem label="Stand">{flightDocument?.stand || 'N/A'}</InfoItem>
                        {!codeShareFlights && <InfoItem label="">{''}</InfoItem>}
                    </div>

                    {/* Code Share Row (if exists) */}
                    {codeShareFlights && (
                        <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                            <InfoItem label="Code Share">{codeShareFlights}</InfoItem>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Aircraft Information Card */}
            {showExtendedInfo && hasAircraftInfo && (
                <Card style={CARD_STYLE}>
                    <Card.Header style={{ backgroundColor: headerBackgroundColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdInfo
                                size={20}
                                color="white"
                            />
                            <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>Aircraft Information</p>
                        </div>
                    </Card.Header>
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={ROW_STYLE}>
                            <InfoItem label="Aircraft Registration">{flightDocument?.aircraftRegistration || 'N/A'}</InfoItem>
                            <InfoItem label="Aircraft Type">{flightDocument?.aircraftType || 'N/A'}</InfoItem>
                            <InfoItem label="Aircraft Model">{flightDocument?.aircraftModel || 'N/A'}</InfoItem>
                        </div>
                        <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                            <InfoItem label="Seats">{flightDocument?.seats || 'N/A'}</InfoItem>
                            <InfoItem label="Seat Capacity">{flightDocument?.seatCapacity || 'N/A'}</InfoItem>
                            <InfoItem label="Ambilift Type">{flightDocument?.ambiliftType || 'N/A'}</InfoItem>
                        </div>
                        <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                            <InfoItem label="Third State Flight">{flightDocument?.thirdStateFlight ? 'True' : 'N/A'}</InfoItem>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Flight Notes Card */}
            <Card style={CARD_STYLE}>
                <Card.Header style={{ backgroundColor: '#6b7280', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdNotes size={20} color="white" />
                        <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>Flight Notes</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {updateFlightStatus === 'loading' && <CustomSpinner style={{ color: 'white' }} />}
                        {saveSuccess && (
                            <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 'bold' }}>
                                ✓ Saved
                            </span>
                        )}
                    </div>
                </Card.Header>
                <Card.Body style={CONTENT_STYLE}>
                    {isEditing ? (
                        <>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={noteInput}
                                onChange={(e) => setNoteInput(e.target.value)}
                                placeholder="Add a new note for this flight..."
                                style={{
                                    fontSize: 14,
                                    borderColor: '#e5e7eb',
                                    borderRadius: 6,
                                    resize: 'vertical',
                                }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: Spacing.SCALE_12 }}>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSaveNotes}
                                    disabled={updateFlightStatus === 'loading' || !noteInput.trim()}
                                >
                                    Add Note
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setNoteInput('');
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div
                            onClick={() => setIsEditing(true)}
                            style={{
                                cursor: 'pointer',
                                padding: Spacing.SCALE_12,
                                backgroundColor: '#f9fafb',
                                borderRadius: 6,
                                border: '1px dashed #d1d5db',
                                minHeight: 80,
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                e.currentTarget.style.borderColor = '#9ca3af';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                                e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                        >
                            {flightNotes.length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: Spacing.SCALE_16 }}>
                                    {flightNotes.map((note, index) => (
                                        <li
                                            key={`${note?.time || note?.timestamp || 'flight-note'}-${index}`}
                                            style={{ marginBottom: index === flightNotes.length - 1 ? 0 : Spacing.SCALE_8 }}
                                        >
                                            <span style={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>
                                                {note?.text}
                                            </span>
                                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                                                {' - '}
                                                {note?.userName || note?.author || 'Unknown'}
                                                {formatFlightNoteTimestamp(note) ? ` - ${formatFlightNoteTimestamp(note)}` : ''}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', fontStyle: 'italic' }}>
                                    Click to add flight notes...
                                </p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Time Locking - for non-boarding flights */}
            {flightDocument?.type !== "boarding" && (
                <Card style={{
                    border: `1px solid ${GRAY_LIGHT}`,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: 'white',
                    marginBottom: Spacing.SCALE_16
                }}>
                    <Card.Header style={{
                        backgroundColor: manualLock ? '#28a745' : '#ffc107',
                        color: 'white',
                        padding: Spacing.SCALE_12,
                        borderRadius: '8px 8px 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {manualLock ? 'Time Locked' : 'Time Unlocked'}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {manualLock ? (
                                <MdLockOutline style={{ fontSize: 20 }} />
                            ) : (
                                <MdLockOpen style={{ fontSize: 20 }} />
                            )}
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: Spacing.SCALE_16 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: Spacing.SCALE_16,
                            marginBottom: Spacing.SCALE_16
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: GRAY_DARK,
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Current Time
                                </div>
                                <div style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: TEXT_COLOR
                                }}>
                                    {currentTime}
                                </div>
                            </div>

                            {!manualLock && (
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: GRAY_DARK,
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        New Time
                                    </div>
                                    <Form.Control
                                        type="time"
                                        value={tempTime}
                                        step={60}
                                        onChange={(e) => setTempTime(e.target.value)}
                                        style={{
                                            maxWidth: 150,
                                            fontSize: '16px',
                                            fontWeight: '500'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <Button
                            variant={manualLock ? "outline-danger" : "primary"}
                            onClick={onPrimaryClick}
                            disabled={patchFlightStatus === "loading"}
                            style={{
                                minWidth: 150,
                                fontWeight: '600'
                            }}
                        >
                            {patchFlightStatus === "loading" ? (
                                <CustomSpinner />
                            ) : (
                                manualLock ? 'Unlock Time' : 'Lock Time'
                            )}
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {/* RC Reports Card — only shown for remote check-in pax */}
            {isRC && rcReports.length > 0 && (
                <Card style={CARD_STYLE}>
                    <Card.Header style={{ backgroundColor: '#6F42C1', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>Remote Check-in Reports</p>
                        <span style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 8px' }}>
                            {rcReports.length}
                        </span>
                    </Card.Header>
                    <Card.Body style={{ padding: 0 }}>
                        {rcReports.map((report, i) => {
                            const ts = report.reportedAt?.toDate
                                ? report.reportedAt.toDate()
                                : report.reportedAt ? new Date(report.reportedAt) : null;
                            const timeStr = ts ? moment(ts).format('HH:mm') : '';
                            return (
                                <div key={i} style={{
                                    padding: '10px 16px',
                                    borderBottom: i < rcReports.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <span style={{
                                            display: 'inline-block',
                                            background: '#ede9fe',
                                            color: '#6F42C1',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            borderRadius: 4,
                                            padding: '2px 7px',
                                            marginBottom: 4,
                                        }}>
                                            {RC_REPORT_LABELS[report.option] ?? report.option}
                                        </span>
                                        {report.note && (
                                            <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>{report.note}</p>
                                        )}
                                    </div>
                                    {timeStr && (
                                        <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{timeStr}</span>
                                    )}
                                </div>
                            );
                        })}
                    </Card.Body>
                </Card>
            )}

        </>
    );
};

export default FlightInformationCard;
